import { LoggerService } from '@angular-ru/cdk/logger';
import { Injectable, inject } from '@angular/core';
import { uniqueId } from 'lodash-es';
import { Subscription, take } from 'rxjs';
import { IShellBackgroundEntity } from 'src/app/api/modules/core/components/abstract/IShellBackgroundEntity';
import { ShellBackgroundEntityModel } from '../components/abstract/shell-background/shell-background-entity/shell-background-entity.model';
import { ShellBackgroundRepository } from '../components/abstract/shell-background/shell-background.repository';
import { ImageLoaderService } from './image-loader.service';

/**
 * The ShellBackgroundService takes an optional {@link IShellBackgroundEntity} configuration from the server
 * and converts it into a validated {@link ShellBackgroundEntityModel} if applicable.
 */
@Injectable({
  providedIn: 'root',
})
export class ShellBackgroundService {
  private readonly shellBackgroundRepository = inject(ShellBackgroundRepository);
  private readonly imageLoaderService = inject(ImageLoaderService);
  private readonly loggerService = inject(LoggerService);

  /**
   * Every time a new configuration is applied to set a background, any existing
   * subscription is canceled.
   */
  private subscription: Subscription | null = null;

  /**
   * An {@link IScreen} may provide the following inputs:
   *
   * - `undefined` - nothing will happen
   * - `null` - will destroy all backgrounds
   * - An {@link IShellBackgroundEntity} which will configure the background.
   *
   * Should an {@link IShellBackgroundEntity} be provided with an image, the image will be preloaded.
   */
  applyConfiguration(configuration?: IShellBackgroundEntity | null): void {
    // if the configuration is undefined, then nothing happens and the method is skipped
    if (configuration === undefined) {
      return;
    }

    // since not undefined, any existing in-flight image preloads are destroyed.
    this.subscription?.unsubscribe();
    this.subscription = null;

    // if the configuration is null
    if (configuration === null) {
      this.applyConfigurationToStore(null);
      return;
    }

    // if the configuration is provided
    if (configuration) {
      // a new model is created
      const model: ShellBackgroundEntityModel = {
        id: uniqueId('shell-background-entity-'),
        color: undefined,
        colorOpacity: 1,
        image: undefined,
        imageOpacity: 1,
        video: [],
        videoOpacity: 1,
      };

      // color and opacity
      const color = configuration.color;
      if (color && color.length > 0) {
        model.color = color;
        if (typeof configuration.colorOpacity === 'number') {
          // you can't have an opacity of 0
          if (configuration.colorOpacity === 0) {
            this.loggerService.warn("Background Color Opacity is '0' and has been disabled");
            model.color = undefined;
          }

          // you can't have an opacity greater than 1 or less than 0
          else if (configuration.colorOpacity >= 0 && configuration.colorOpacity <= 1) {
            model.colorOpacity = configuration.colorOpacity;
          } else {
            this.loggerService.warn('Background Color Opacity must be between 0 and 1');
          }
        }
      }

      // video and opacity
      const video = configuration.video;
      if (video && video.length > 0) {
        model.video = video.map((source) => ({ ...source }));

        // check that the video entry is valid, if it's not then video cannot be used.
        video.forEach((source) => {
          if (!source.src || !source.type) {
            model.video = [];
            this.loggerService.warn('Background Video is invalid and has been disabled');
          }
        });

        if (typeof configuration.videoOpacity === 'number') {
          // you can't have an opacity of 0
          if (configuration.videoOpacity === 0) {
            model.video = [];
            this.loggerService.warn("Background Video Opacity is '0' and has been disabled");
          }

          // you can't have an opacity greater than 1 or less than 0
          else if (configuration.videoOpacity >= 0 && configuration.videoOpacity <= 1) {
            model.videoOpacity = configuration.videoOpacity;
          } else {
            this.loggerService.warn('Background Video Opacity must be between 0 and 1');
          }
        }
      }

      // image and opacity
      const image = configuration.image;
      if (image && image.length > 0) {
        model.image = `url("${image}")`;
        if (typeof configuration.imageOpacity === 'number') {
          // you can't have an opacity of 0
          if (configuration.imageOpacity === 0) {
            model.image = undefined;
            this.loggerService.warn("Background Image Opacity is '0' and has been disabled");
          }

          // you can't have an opacity greater than 1 or less than 0
          else if (configuration.imageOpacity >= 0 && configuration.imageOpacity <= 1) {
            model.imageOpacity = configuration.imageOpacity;
          } else {
            this.loggerService.warn('Background Image Opacity must be between 0 and 1');
          }
        }

        // preload image to cache — singular (strict) loader: on failure the
        // error branch fires and the new background is never applied, so the
        // existing one persists.
        this.subscription = this.imageLoaderService
          .loadImageToCache(image)
          .pipe(take(1))
          .subscribe({
            complete: () => {
              this.applyConfigurationToStore(model);
            },
            error: () => {
              // we can safely ignore errors because this will just persist the existing background.
            },
          });
      } else {
        this.applyConfigurationToStore(model);
      }
    }
  }

  /**
   * Applies the processed configuration to the store.
   *
   * @param model - the model to apply.
   */
  private applyConfigurationToStore(model: ShellBackgroundEntityModel | null): void {
    this.shellBackgroundRepository.applyConfiguration(model);
  }
}
